// Cee-lo (docs/design/HUB.md §Room 4): the Army barracks dice game, played
// on a wool blanket by off-duty operators. Three dice: 4-5-6 wins outright,
// triples beat points, a pair sets the odd die as your point, 1-2-3 loses
// outright. Stakes are matchsticks — the doc's hard rule is that this can
// NEVER touch meta progression, so nothing here persists or pays.

export type CeeloHand =
  | { kind: "456" } // instant win
  | { kind: "triple"; value: number }
  | { kind: "point"; value: number }
  | { kind: "123" } // instant loss
  | { kind: "nothing" }; // no pair — reroll

export type CeeloRoll = {
  dice: [number, number, number];
  hand: CeeloHand;
};

export function scoreDice(dice: [number, number, number]): CeeloHand {
  const s = [...dice].sort((a, b) => a - b) as [number, number, number];
  if (s[0] === 4 && s[1] === 5 && s[2] === 6) return { kind: "456" };
  if (s[0] === 1 && s[1] === 2 && s[2] === 3) return { kind: "123" };
  if (s[0] === s[1] && s[1] === s[2]) return { kind: "triple", value: s[0] };
  if (s[0] === s[1]) return { kind: "point", value: s[2] };
  if (s[1] === s[2]) return { kind: "point", value: s[0] };
  return { kind: "nothing" };
}

// Roll until the hand resolves (a "nothing" makes you throw again on the
// blanket). Capped so a cursed RNG can't spin forever.
export function rollUntilResolved(rng: () => number, maxThrows = 20): CeeloRoll {
  let dice: [number, number, number] = [1, 1, 1];
  for (let i = 0; i < maxThrows; i++) {
    dice = [
      1 + Math.floor(rng() * 6),
      1 + Math.floor(rng() * 6),
      1 + Math.floor(rng() * 6),
    ];
    const hand = scoreDice(dice);
    if (hand.kind !== "nothing") return { dice, hand };
  }
  return { dice, hand: scoreDice(dice) };
}

// Rank a resolved hand for comparison. 456 > triples (by face) > points
// (by face) > 123. "nothing" never reaches here in a resolved game.
function handRank(hand: CeeloHand): number {
  switch (hand.kind) {
    case "456": return 100;
    case "triple": return 50 + hand.value;
    case "point": return 10 + hand.value;
    case "123": return 0;
    case "nothing": return -1;
  }
}

export type CeeloResult = {
  player: CeeloRoll;
  opponent: CeeloRoll;
  outcome: "win" | "lose" | "push";
};

export function playCeelo(rng: () => number): CeeloResult {
  const player = rollUntilResolved(rng);
  const opponent = rollUntilResolved(rng);
  const p = handRank(player.hand);
  const o = handRank(opponent.hand);
  return {
    player,
    opponent,
    outcome: p > o ? "win" : p < o ? "lose" : "push",
  };
}

export function handLabel(hand: CeeloHand): string {
  switch (hand.kind) {
    case "456": return "4-5-6! HEAD CRACK";
    case "triple": return `TRIPS ${hand.value}s`;
    case "point": return `POINT ${hand.value}`;
    case "123": return "1-2-3. OUCH";
    case "nothing": return "NO HAND";
  }
}

export const DIE_FACES = ["⚀", "⚁", "⚂", "⚃", "⚄", "⚅"] as const;

export function diceFaces(dice: [number, number, number]): string {
  return dice.map((d) => DIE_FACES[d - 1]).join(" ");
}
